import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import style from './CategoryPanel.module.scss';

import ModalStore from 'stores/ModalStore';
import CategoriesStore from 'stores/Menu/CategoriesStore';

import CategoryModal from 'components/Modals/Category/CategoryModal';
import Icon from 'components/Icon/Icon';
import Button from 'components/Button/Button';
import WarningModal from 'components/Modals/Warning/WarningModal';
import FeedbackDishGroupsModal from 'components/Modals/Feedback/MenuItems/FeedbackMenuItemsModal';
import TooltipComponent from 'components/Tooltip/Tooltip';

interface Props {
  store: CategoriesStore;
}

@observer
class CategoryPanel extends Component<Props> {
  handleClick = (id?) => () => {
    this.props.store.changeCategory(id);
  };

  handleCategoryEdit = category => async () => {
    const { payload } = await ModalStore.showModal(CategoryModal, { category });
    this.props.store.updateCategories(payload);
  };

  handleCategoryDelete = id => async () => {
    await this.props.store.deleteCategory(id);
  };

  handleCategoryKioskSelfCheckoutVisibilityChange = category => async () => {
    await ModalStore.showModal(WarningModal, {
      title: category.notVisibleKioskSelfCheckout
        ? 'modal.warning.title.confirm.display'
        : 'modal.warning.title.confirm.hide',
      description: category.notVisibleKioskSelfCheckout
        ? 'modal.warning.confirm.display'
        : 'modal.warning.confirm.hide',
      type: 'change',
    });
    await this.props.store.changeCategoryKioskSelfCheckoutVisibility(category);
  };

  handleFeedbackClick = () => {
    ModalStore.showModal(FeedbackDishGroupsModal);
  };

  render() {
    const { store } = this.props;
    const empty = !Boolean(store.categories.length);

    return (
      <div className={style.panel}>
        <div className={style.panel__header}>
          <div className={style.panel__header_title}>
            <FormattedMessage id="title.feedback" />
          </div>
          <TooltipComponent
            content={<FormattedMessage id="title.feedback.hint" />}
          >
            <div className={style.panel__header_feedback}>
              <Button styleType="text" onClick={this.handleFeedbackClick}>
                <Icon type="feedback" />
              </Button>
            </div>
          </TooltipComponent>
        </div>

        <div className={style.panel__header}>
          <div className={style.panel__header_title}>
            <FormattedMessage id="title.category" />
          </div>
          {store.activeCategoryID && (
            <Button
              styleType="text"
              onClick={this.handleClick()}
              className={style.panel__header_showAll}
            >
              <FormattedMessage id="title.showAllDishes" />
            </Button>
          )}
        </div>

        <ul
          className={classNames({
            [style.list__empty]: empty,
          })}
        >
          {empty ? (
            <pre className={style.wrapper__empty}>
              <Icon type="plate" className={style.icon__plate} />
              <FormattedMessage id="category.empty" />
            </pre>
          ) : (
            store.categories.map(category => (
              <li
                className={classNames(style.category, {
                  [style.category__active]:
                    category.id === store.activeCategoryID,
                })}
                key={category.id}
                onClick={this.handleClick(category.id)}
              >
                <div className={style.category__titleWrapper}>
                  <span className={style.category__title}>
                    {category.title}
                  </span>

                  {category.standardMenuItems && (
                    <span className={style.category__standardArticlesTitle}>
                      <FormattedMessage id="category.standard.articles" />
                    </span>
                  )}
                </div>

                <span className={style.code}>{category.itemNumber}</span>

                <div className={style.controls}>
                  <Button
                    styleType="text"
                    className={style.control}
                    onClick={this.handleCategoryKioskSelfCheckoutVisibilityChange(
                      category,
                    )}
                  >
                    <Icon
                      type={
                        category.notVisibleKioskSelfCheckout
                          ? 'iconHide'
                          : 'iconShow'
                      }
                    />
                  </Button>
                  <Button
                    styleType="text"
                    className={style.control}
                    onClick={this.handleCategoryEdit(category)}
                  >
                    <Icon type="edit" />
                  </Button>
                  <Button
                    styleType="text"
                    className={style.control}
                    onClick={this.handleCategoryDelete(category.id)}
                  >
                    <Icon type="trash" />
                  </Button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    );
  }
}

export default CategoryPanel;
